/**
 * Field-type catalogue and per-field definition shared by the builder, the
 * public renderer, and the Worker. Field definitions are stored inside an
 * immutable `form_versions.schema` JSON array.
 */

export const FIELD_TYPES = [
  'short_text',
  'long_text',
  'email',
  'phone',
  'number',
  'date',
  'select',
  'radio',
  'checkbox',
  'multiselect',
  'address',
  'vat',
  'company_number',
  'sort_code',
  'file',
  'repeater',
  'contact',
  'heading',
  'consent',
  'hidden',
] as const;

export type FieldType = (typeof FIELD_TYPES)[number];

/** Whether a field is shown to the applicant or only to staff during approval. */
export type FieldAudience = 'customer' | 'staff';

export type FieldGroup = 'Basics' | 'Choice' | 'Specialised' | 'Layout';

/** Palette metadata for the builder. */
export interface FieldTypeMeta {
  type: FieldType;
  label: string;
  icon: string;
  group: FieldGroup;
  /** Plain-English name for office staff (F9), e.g. "Pick one". */
  plainName: string;
  /** One line on what it does for the applicant. */
  description: string;
  /** An example question from the JFH forms. */
  example: string;
}

export const FIELD_TYPE_CATALOGUE: readonly FieldTypeMeta[] = [
  { type: 'short_text', label: 'Short text', icon: 'text', group: 'Basics', plainName: 'Short answer', description: 'One line of text.', example: 'Trading name' },
  { type: 'long_text', label: 'Paragraph', icon: 'paragraph', group: 'Basics', plainName: 'Long answer', description: 'A few sentences or more.', example: 'Anything else we should know?' },
  { type: 'email', label: 'Email', icon: 'mail', group: 'Basics', plainName: 'Email address', description: 'Checks it looks like an email address.', example: 'Accounts email' },
  { type: 'phone', label: 'Phone', icon: 'phone', group: 'Basics', plainName: 'Phone number', description: 'With a country code picker.', example: 'Mobile' },
  { type: 'number', label: 'Number', icon: 'hash', group: 'Basics', plainName: 'Number', description: 'Digits only.', example: 'Years trading' },
  { type: 'date', label: 'Date', icon: 'calendar', group: 'Basics', plainName: 'Date', description: 'A date picked from a calendar.', example: 'Date the business started' },
  { type: 'select', label: 'Dropdown', icon: 'chevron', group: 'Choice', plainName: 'Dropdown list', description: 'Pick one answer. Best for longer lists.', example: 'Business type' },
  { type: 'radio', label: 'Radio', icon: 'radio', group: 'Choice', plainName: 'Pick one', description: 'All answers on show, pick one. Best for 2 to 5.', example: 'What do you supply?' },
  { type: 'checkbox', label: 'Checkbox', icon: 'check', group: 'Choice', plainName: 'Tick boxes', description: 'A set of tick boxes, or one yes/no tick.', example: 'Same as delivery address' },
  { type: 'multiselect', label: 'Multi-select', icon: 'list', group: 'Choice', plainName: 'Pick any', description: 'Tick as many answers as apply.', example: 'Interested in' },
  { type: 'address', label: 'Address', icon: 'pin', group: 'Specialised', plainName: 'Address', description: 'An address, with the lines you choose.', example: 'Delivery address' },
  { type: 'vat', label: 'VAT number', icon: 'badge', group: 'Specialised', plainName: 'VAT number', description: 'A UK VAT number.', example: 'VAT number' },
  { type: 'company_number', label: 'Company number', icon: 'badge', group: 'Specialised', plainName: 'Company number', description: 'Checks Companies House, then fills in and locks the company name and registered address.', example: 'Company registration number' },
  { type: 'sort_code', label: 'Sort code', icon: 'hash', group: 'Specialised', plainName: 'Sort code', description: 'Formats as 00-00-00 while they type.', example: 'Sort code' },
  { type: 'file', label: 'File upload', icon: 'upload', group: 'Specialised', plainName: 'File upload', description: 'A document or photo.', example: 'Latest filed accounts' },
  { type: 'repeater', label: 'Repeatable group', icon: 'copy', group: 'Specialised', plainName: 'Repeating group', description: 'The same few questions, answered as many times as needed.', example: 'Trade references' },
  { type: 'contact', label: 'Contact', icon: 'user', group: 'Specialised', plainName: 'Contacts', description: 'One or more people, one marked as the main contact.', example: 'Contacts' },
  { type: 'heading', label: 'Section heading', icon: 'heading', group: 'Layout', plainName: 'Step', description: 'Starts a new page of the form, with its own title.', example: 'Credit & banking' },
  { type: 'consent', label: 'Consent', icon: 'shield', group: 'Layout', plainName: 'Agreement', description: 'A tick box to agree to your terms. The full text opens in a pop-up.', example: 'I agree to the JFH terms of trade' },
  { type: 'hidden', label: 'Hidden field', icon: 'eye-off', group: 'Layout', plainName: 'Hidden value', description: 'Stored with every application, never shown.', example: 'Source = website' },
] as const;

/**
 * Format a UK sort code as `XX-XX-XX`: keep digits only (max 6) and group them
 * in pairs with dashes. Partial input formats as you type (e.g. `1234` →
 * `12-34`). Used by the `sort_code` field type.
 */
export function formatSortCode(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 6);
  return digits.replace(/(\d{2})(?=\d)/g, '$1-');
}

export interface FieldValidation {
  /** Regex source string applied to the stringified value. */
  pattern?: string;
  /** Numeric bounds (number fields). */
  min?: number;
  max?: number;
  /** Length bounds (text fields). */
  minLength?: number;
  maxLength?: number;
}

/** Comparison operators for conditional-visibility rules. */
export type ConditionOp =
  | 'eq' // equals
  | 'neq' // not equals
  | 'contains' // string/array contains value
  | 'in' // value is one of a comma-separated list
  | 'gt' // numeric greater than
  | 'lt' // numeric less than
  | 'empty' // no value
  | 'not_empty'; // has a value

export interface VisibilityRule {
  fieldId: string;
  op: ConditionOp;
  /** Comparison value (unused for empty/not_empty). */
  value?: string;
}

/**
 * One entry in a rule list: either a leaf condition or a nested group. Nesting
 * lets visibility express "OR of ANDs" (e.g. show when ⟨EU country AND not GB⟩
 * OR region = NI OR region = SI), which a single flat match mode can't.
 */
export type VisibilityNode = VisibilityRule | ConditionalVisibility;

/**
 * Show a field only when its rules pass. `match: 'all'` = every entry (AND),
 * `'any'` = at least one (OR). Entries may be leaf rules or nested groups. An
 * empty rule set is always visible.
 */
export interface ConditionalVisibility {
  match: 'all' | 'any';
  rules: VisibilityNode[];
}

/** A rule-list entry is a nested group when it carries its own `rules` array. */
export function isVisibilityGroup(node: VisibilityNode): node is ConditionalVisibility {
  return (
    typeof node === 'object' &&
    node !== null &&
    Array.isArray((node as ConditionalVisibility).rules)
  );
}

/** Legacy single-condition shape, still accepted by the evaluator. */
interface LegacyVisibility {
  fieldId: string;
  equals: string;
}

/** Selectable comparison operators, with labels, for rule-builder UIs. */
export const CONDITION_OPS: { value: ConditionOp; label: string }[] = [
  { value: 'eq', label: 'equals' },
  { value: 'neq', label: "doesn't equal" },
  { value: 'contains', label: 'contains' },
  { value: 'in', label: 'is one of (comma-sep)' },
  { value: 'gt', label: 'greater than' },
  { value: 'lt', label: 'less than' },
  { value: 'empty', label: 'is empty' },
  { value: 'not_empty', label: 'is not empty' },
];

/** Operators that don't take a comparison value. */
export const CONDITION_OPS_NO_VALUE: ConditionOp[] = ['empty', 'not_empty'];

/** Compare a raw value against `cmp` using a condition operator. */
export function matchOp(op: ConditionOp, raw: unknown, cmp = ''): boolean {
  const has =
    raw !== undefined &&
    raw !== null &&
    !(typeof raw === 'string' && raw.trim() === '') &&
    !(Array.isArray(raw) && raw.length === 0);
  const str = Array.isArray(raw) ? raw.join(',') : String(raw ?? '');
  switch (op) {
    case 'eq':
      return str === cmp;
    case 'neq':
      return str !== cmp;
    case 'contains':
      return Array.isArray(raw)
        ? raw.map(String).includes(cmp)
        : str.toLowerCase().includes(cmp.toLowerCase());
    case 'in':
      return cmp
        .split(',')
        .map((s) => s.trim())
        .includes(str);
    case 'gt':
      return Number(raw) > Number(cmp);
    case 'lt':
      return Number(raw) < Number(cmp);
    case 'empty':
      return !has;
    case 'not_empty':
      return has;
    default:
      return true;
  }
}

/**
 * Resolve a (possibly nested) field id against the values. A plain id reads the
 * top-level value; a dotted id reads into an object — e.g. `addr.country` reads
 * the country sub-field of the `addr` address field. An exact top-level key wins
 * (field ids never contain dots).
 */
export function valueAtPath(values: Record<string, unknown>, path: string): unknown {
  if (path in values) return values[path];
  let cur: unknown = values;
  for (const seg of path.split('.')) {
    if (cur === null || typeof cur !== 'object') return undefined;
    cur = (cur as Record<string, unknown>)[seg];
  }
  return cur;
}

/**
 * Expose the selected country's lookup attributes under synthetic keys
 * `<fieldId>.country.<attr>` (e.g. `…country.D_EUCountry`), for each top-level
 * `address` field and each dropdown / choice question using the iQ country
 * list, so visibility/default rules can condition on a country attribute from
 * iQ's /countries service. Returns a new object only when something is added.
 */
export function augmentCountryAttributes(
  values: Record<string, unknown>,
  schema: FieldDef[],
  countryData: Record<string, Record<string, string>>,
): Record<string, unknown> {
  let out: Record<string, unknown> | null = null;
  for (const f of schema) {
    let code: unknown;
    if (f.type === 'address') {
      const addr = values[f.id];
      code = addr && typeof addr === 'object' && !Array.isArray(addr) ? (addr as Record<string, unknown>).country : undefined;
    } else if (isCountryQuestion(f)) {
      code = values[f.id];
    } else {
      continue;
    }
    if (typeof code !== 'string') continue;
    const data = countryData[code];
    if (!data) continue;
    out = out ?? { ...values };
    for (const [k, v] of Object.entries(data)) out[`${f.id}.country.${k}`] = v;
  }
  return out ?? values;
}

/** A dropdown or choice question whose answers are iQ's country list (codes). */
export function isCountryQuestion(f: Pick<FieldDef, 'type' | 'optionsSource'>): boolean {
  return (f.type === 'select' || f.type === 'radio') && f.optionsSource?.key === 'countries';
}

/**
 * Pre-fill the Country line of addresses linked to a country question
 * (`address.countryFrom`), top-level and inside contacts. An address takes the
 * question's answer when its country is empty, or when it still holds the
 * question's previous answer (so the applicant's own choice is kept). Only
 * addresses asking for Country, only answers in `known` (country codes), and
 * never "same as" or `skip` (locked) addresses. Returns `next` unchanged when
 * nothing is filled.
 */
export function applyCountryDefaults(
  schema: FieldDef[],
  prev: Record<string, unknown>,
  next: Record<string, unknown>,
  known: ReadonlySet<string>,
  skip: ReadonlySet<string> = new Set(),
): Record<string, unknown> {
  const answer = (v: unknown) => (typeof v === 'string' && known.has(v) ? v : '');
  const asksCountry = (address: AddressConfig | undefined) =>
    resolveAddressComponents({ address }).some((c) => c.key === 'country' && c.enabled);
  /** The address with its country filled, or null to leave it. */
  const fill = (address: AddressConfig | undefined, value: unknown): Record<string, unknown> | null => {
    const from = address?.countryFrom;
    if (!from || !asksCountry(address)) return null;
    const now = answer(next[from]);
    if (!now) return null;
    // A whole (blank) address, as the renderer only reads complete ones.
    const addr = {
      ...Object.fromEntries(ADDRESS_COMPONENT_ORDER.map((k) => [k, ''])),
      ...(value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {}),
    };
    if (addr.sameAsFirst === true) return null;
    const cur = typeof addr.country === 'string' ? addr.country : '';
    if (cur === now) return null;
    if (cur && cur !== answer(prev[from])) return null;
    return { ...addr, country: now };
  };

  let out: Record<string, unknown> | null = null;
  for (const f of schema) {
    if (skip.has(f.id)) continue;
    if (f.type === 'address') {
      const addr = fill(f.address, next[f.id]);
      if (addr) (out ??= { ...next })[f.id] = addr;
    } else if (isItemListField(f.type) && Array.isArray(next[f.id])) {
      const subs = resolveRepeaterSubFields(f).filter((sub) => sub.type === 'address');
      if (!subs.length) continue;
      let changed = false;
      const items = (next[f.id] as RepeaterItem[]).map((it) => {
        let item = it;
        for (const sub of subs) {
          const addr = fill(sub.address, item?.[sub.id]);
          if (addr) {
            item = { ...item, [sub.id]: addr };
            changed = true;
          }
        }
        return item;
      });
      if (changed) (out ??= { ...next })[f.id] = items;
    }
  }
  return out ?? next;
}

/**
 * Start contact / repeater lines as another question's answer
 * (`RepeaterSubField.defaultFrom`): a line takes the answer when it's empty,
 * or when it still holds the question's previous answer, so what the
 * applicant typed is kept. Returns `next` unchanged when nothing is filled.
 */
export function applySubFieldDefaults(
  schema: FieldDef[],
  prev: Record<string, unknown>,
  next: Record<string, unknown>,
): Record<string, unknown> {
  const text = (v: unknown) => (typeof v === 'string' ? v : '');
  let out: Record<string, unknown> | null = null;
  for (const f of schema) {
    if (!isItemListField(f.type)) continue;
    const all = resolveRepeaterSubFields(f);
    const subs = all.filter((s) => s.defaultFrom && s.type !== 'address');
    if (!subs.length) continue;
    // Required entries show before anything is typed but aren't stored yet:
    // create them (blank) so they start with the answer too. Optional ones
    // take it once the applicant starts filling them in.
    const min = f.minItems ?? (f.required ? 1 : 0);
    const stored = Array.isArray(next[f.id]) ? (next[f.id] as RepeaterItem[]) : [];
    const current =
      stored.length || min < 1 ? stored : Array.from({ length: min }, () => Object.fromEntries(all.map((s) => [s.id, ''])));
    if (!current.length) continue;
    let changed = false;
    const items = current.map((it) => {
      let item = it;
      for (const s of subs) {
        const now = text(next[s.defaultFrom!]).trim();
        if (!now) continue;
        const cur = text(item?.[s.id]);
        if (cur === now || (cur.trim() && cur !== text(prev[s.defaultFrom!]).trim())) continue;
        item = { ...item, [s.id]: now };
        changed = true;
      }
      return item;
    });
    if (changed) (out ??= { ...next })[f.id] = items;
  }
  return out ?? next;
}

function evalRule(rule: VisibilityRule, values: Record<string, unknown>): boolean {
  return matchOp(rule.op, valueAtPath(values, rule.fieldId), rule.value ?? '');
}

/** Evaluate one rule-list entry — recursing into nested groups. */
function evalNode(node: VisibilityNode, values: Record<string, unknown>): boolean {
  return isVisibilityGroup(node) ? evalVisibility(node, values) : evalRule(node, values);
}

/** Evaluate a field's visibility against the current form values. */
export function evalVisibility(
  vis: ConditionalVisibility | LegacyVisibility | undefined,
  values: Record<string, unknown>,
): boolean {
  if (!vis) return true;
  // Legacy single-condition shape.
  if ('equals' in vis) {
    return String(valueAtPath(values, vis.fieldId) ?? '') === vis.equals;
  }
  const rules = vis.rules ?? [];
  if (rules.length === 0) return true;
  return vis.match === 'any'
    ? rules.some((r) => evalNode(r, values))
    : rules.every((r) => evalNode(r, values));
}

/**
 * Drop every value belonging to a currently-hidden field (its top-level key and
 * any dotted sub-paths, e.g. `addr.country.D_EUCountry`), so a rule that reads a
 * hidden field sees no value. Returns the input unchanged when nothing is masked.
 */
function maskHidden(
  values: Record<string, unknown>,
  schema: FieldDef[],
  visible: Record<string, boolean>,
): Record<string, unknown> {
  let out = values;
  for (const f of schema) {
    if (visible[f.id]) continue;
    for (const key of Object.keys(out)) {
      if (key === f.id || key.startsWith(`${f.id}.`)) {
        if (out === values) out = { ...values };
        delete out[key];
      }
    }
  }
  return out;
}

/** Whether a value counts as answered: not empty, an empty list or an unticked box. */
export function isAnswered(v: unknown): boolean {
  if (v === undefined || v === null || v === false) return false;
  if (typeof v === 'string') return v.trim() !== '';
  if (Array.isArray(v)) return v.length > 0;
  return true;
}

/**
 * Resolve visibility for every field at once, cascading: when a field's rule
 * references another field that is itself hidden, that driver is treated as
 * having no value — so its dependents hide too (e.g. a "TSS Code" shown only when
 * "Region" = Northern Ireland disappears once "Region" is hidden by its own
 * country rule). Iterates to a fixed point so chains (A→B→C) settle.
 */
export function visibleFields(
  schema: FieldDef[],
  values: Record<string, unknown>,
): Record<string, boolean> {
  const visible: Record<string, boolean> = {};
  for (const f of schema) visible[f.id] = true;
  // An applicant question sits on the step its last heading starts; a hidden
  // step (heading with its own rule) hides every question on it. Questions
  // after an "answer this first" question wait until it's answered.
  const stepOf: Record<string, string> = {};
  const gatesBefore: Record<string, string[]> = {};
  let head: string | null = null;
  let gates: string[] = [];
  for (const f of schema) {
    if (f.audience === 'staff') continue;
    if (f.type === 'heading') {
      head = f.id;
      gates = [];
      continue;
    }
    if (head) stepOf[f.id] = head;
    if (gates.length) gatesBefore[f.id] = gates;
    if (f.answerFirst) gates = [...gates, f.id];
  }
  // At most one field can flip per pass in the worst case; +1 guards the bound.
  for (let pass = 0; pass <= schema.length; pass++) {
    const effective = maskHidden(values, schema, visible);
    let changed = false;
    for (const f of schema) {
      const step = stepOf[f.id];
      const v =
        evalVisibility(f.visibleWhen, effective) &&
        (!step || visible[step] !== false) &&
        (gatesBefore[f.id] ?? []).every((g) => !visible[g] || isAnswered(effective[g]));
      if (v !== visible[f.id]) {
        visible[f.id] = v;
        changed = true;
      }
    }
    if (!changed) break;
  }
  return visible;
}

/** One "when the driver field <op> X, default to Y" rule (conditional default). */
export interface DefaultRule {
  op?: ConditionOp;
  equals: string;
  value: string;
}

export interface FieldDef {
  id: string;
  type: FieldType;
  label: string;
  audience: FieldAudience;
  help?: string;
  placeholder?: string;
  required?: boolean;
  /** Simple options for select / radio / checkbox / multiselect (value = label). */
  options?: string[];
  /** Value/label option pairs — when the stored value differs from the display. */
  choices?: FieldOption[];
  /** Dynamic option source (e.g. an iQ list). Resolved server-side with caching. */
  optionsSource?: OptionsSource;
  /**
   * For choice fields (select / radio / multiselect): offer an "Other" option
   * that reveals a free-text input so applicants can supply their own answer.
   */
  allowOther?: boolean;
  validation?: FieldValidation;
  /**
   * Conditional visibility — show only when `visibleWhen` is satisfied. On a
   * `heading`, it shows or skips the whole step.
   */
  visibleWhen?: ConditionalVisibility;
  /**
   * Applicant questions: the rest of this step stays hidden until this one is
   * answered (e.g. country first, then the questions that depend on it).
   */
  answerFirst?: boolean;
  /**
   * For STAFF fields on two-stage (credit) forms: which approval stage completes
   * this field — `cs` (Customer Service, stage 1) or `accounts` (stage 2).
   * Unset = Customer Service. Ignored for customer fields and single-stage forms.
   */
  staffStage?: 'cs' | 'accounts';
  /**
   * Highlight this field in the summary block at the top of the stage-2
   * (credit-approval / Accounts) Teams card. No effect on the stage-1 card.
   */
  cardSummary?: boolean;
  /** Default value for hidden fields. */
  defaultValue?: string;
  /**
   * Conditional default: when the driver field (`defaultWhen.fieldId`) matches a
   * rule, this field's default becomes that rule's value — pre-filling it on the
   * Teams card / approval (staff can still change it). Falls back to
   * `defaultValue` when no rule matches. E.g. Supplier Type = Goods → Net
   * Purchases Account = Stock Purchase Accruals.
   */
  defaultWhen?: { fieldId: string; rules: DefaultRule[] };
  /**
   * For Wix-linked forms: which incoming Wix value fills this field — a Wix
   * field key (`first_name` / `field:first_name`), a submission label
   * (`Email Address`), or a contact path (`contact.email`, `contact.name.first`,
   * `contact.company`, `contact.phone`).
   */
  wixKey?: string;
  /** Per-instance configuration for `address` fields (which lines show + required). */
  address?: AddressConfig;
  /**
   * For `company_number` fields: the fields a Companies House lookup fills and
   * locks — the company name (short text) and registered address (address).
   */
  companyLookup?: { nameFieldId?: string; addressFieldId?: string };
  /**
   * Long-form body for `consent` fields — e.g. the trade or credit terms &
   * conditions text the applicant is agreeing to. Shown in a scrollable box
   * above the consent checkbox.
   */
  content?: string;
  /** Sub-fields for a `repeater` group (e.g. multiple contacts). */
  subFields?: RepeaterSubField[];
  /** Singular label for one repeater item, e.g. "Contact". */
  itemLabel?: string;
  /** Min / max repeater items (default min 1, max unlimited). */
  minItems?: number;
  maxItems?: number;
}

/** A section heading splits the public form into a step/page. */
export function isSectionBreak(field: FieldDef): boolean {
  return field.type === 'heading';
}

/** A choice with a distinct stored value and display label. */
export interface FieldOption {
  value: string;
  label: string;
  /**
   * Extra per-option values captured from a lookup (API mode), keyed by the
   * lookup's configured extra-field name — e.g. a currency's `postingMap`. Used
   * by the iQ `lookup` mapping source; not shown to applicants.
   */
  data?: Record<string, string>;
}

/** Dynamic option source. `iq` lists are fetched + cached by the Worker. */
export interface OptionsSource {
  type: 'iq';
  /** Identifies the list, e.g. "sales_reps", "pricing_tiers". */
  key: string;
}

/**
 * A field's effective default given the current values: the matching
 * `defaultWhen` rule's value, else the static `defaultValue`. Used to pre-fill
 * the Teams card and the in-portal approval form.
 */
export function resolveFieldDefault(
  field: Pick<FieldDef, 'defaultValue' | 'defaultWhen'>,
  values: Record<string, unknown>,
): string | undefined {
  const dw = field.defaultWhen;
  if (dw?.fieldId && dw.rules?.length) {
    const raw = valueAtPath(values, dw.fieldId);
    const hit = dw.rules.find((r) => matchOp(r.op ?? 'eq', raw, r.equals));
    if (hit) return hit.value;
  }
  return field.defaultValue;
}

/** Static options as value/label pairs (`choices` wins over `options`). */
export function resolveStaticOptions(
  field: Pick<FieldDef, 'choices' | 'options'>,
): FieldOption[] {
  if (field.choices && field.choices.length) return field.choices;
  return (field.options ?? []).map((o) => ({ value: o, label: o }));
}

// ---------------------------------------------------------------------------
// Repeater (repeatable group) / contact configuration — e.g. multiple contacts
// ---------------------------------------------------------------------------

/**
 * A field type usable inside a repeater / contact item. `address` is a
 * composite sub-field (only meaningful inside a `contact`) carrying its own
 * `AddressConfig`; everything else maps to a single text-style input.
 */
export type RepeaterSubFieldType =
  | 'short_text'
  | 'long_text'
  | 'email'
  | 'phone'
  | 'number'
  | 'date'
  | 'select'
  | 'address';

export interface RepeaterSubField {
  id: string;
  type: RepeaterSubFieldType;
  label: string;
  required?: boolean;
  placeholder?: string;
  options?: string[];
  /** Per-component config when `type === 'address'`. */
  address?: AddressConfig;
  /**
   * A question whose answer each item's value starts as (e.g. a contact's
   * Company name from the form's company name, which Companies House may
   * fill). The applicant can still change it. See `applySubFieldDefaults`.
   */
  defaultFrom?: string;
  /**
   * Explicit iQ Contacts[] property name for this sub-field (e.g. `EmailAddress`,
   * `JobTitle`). Overrides the name-based heuristic — needed for custom sub-fields
   * so they post to iQ under the right property.
   */
  iqField?: string;
}

/** One repeater / contact item's values, keyed by sub-field id. */
export type RepeaterItem = Record<string, unknown>;

/** Field types whose value is a list of sub-field items (array of objects). */
export function isItemListField(type: FieldType): boolean {
  return type === 'repeater' || type === 'contact';
}

/**
 * Reserved meta key on a `contact` item marking it as the primary contact.
 * It is not a sub-field, so it never renders as an input or maps into iQ as a
 * value — display/serialisation paths filter it out.
 */
export const CONTACT_PRIMARY_KEY = '_primary';

/** Whether a key on a contact item is reserved meta (not a sub-field value). */
export function isContactMetaKey(key: string): boolean {
  return key === CONTACT_PRIMARY_KEY;
}

/** Index of the contact flagged primary, defaulting to 0 (the first). */
export function primaryContactIndex(items: RepeaterItem[]): number {
  const i = items.findIndex((it) => it?.[CONTACT_PRIMARY_KEY] === true);
  return i >= 0 ? i : 0;
}

export function defaultRepeaterSubFields(): RepeaterSubField[] {
  return [
    { id: 'name', type: 'short_text', label: 'Full name', required: true },
    { id: 'email', type: 'email', label: 'Email', required: true },
    { id: 'phone', type: 'phone', label: 'Phone' },
  ];
}

/** Default sub-fields for a `contact` — the primary-contact section shape. */
export function defaultContactSubFields(): RepeaterSubField[] {
  return [
    { id: 'firstName', type: 'short_text', label: 'First name', required: true },
    { id: 'lastName', type: 'short_text', label: 'Last name', required: true },
    { id: 'email', type: 'email', label: 'Email', required: true },
    { id: 'phone', type: 'phone', label: 'Phone' },
    { id: 'mobile', type: 'phone', label: 'Mobile' },
    { id: 'address', type: 'address', label: 'Address' },
  ];
}

/** Repeater / contact sub-fields with a type-appropriate default when unset. */
export function resolveRepeaterSubFields(
  field: Pick<FieldDef, 'subFields' | 'type'>,
): RepeaterSubField[] {
  if (field.subFields && field.subFields.length) return field.subFields;
  return field.type === 'contact'
    ? defaultContactSubFields()
    : defaultRepeaterSubFields();
}

/**
 * The primary contact's value for the first sub-field matching `match` (e.g. its
 * email). Returns undefined when there's no contact field, no contacts, or the
 * sub-field is blank.
 */
export function primaryContactValue(
  schema: FieldDef[],
  data: Record<string, unknown>,
  match: (s: RepeaterSubField) => boolean,
): string | undefined {
  const contact = schema.find((f) => f.type === 'contact');
  if (!contact) return undefined;
  const raw = data[contact.id];
  if (!Array.isArray(raw)) return undefined;
  const items = raw.filter(
    (it): it is RepeaterItem => it !== null && typeof it === 'object' && !Array.isArray(it),
  );
  if (!items.length) return undefined;
  const primary = items[primaryContactIndex(items)] ?? items[0]!;
  const sub = resolveRepeaterSubFields(contact).find(match);
  const v = sub ? primary[sub.id] : undefined;
  return typeof v === 'string' && v.trim() ? v.trim() : undefined;
}

/**
 * Recipient email for applicant notifications: the primary contact's email,
 * falling back to a top-level customer email field (older forms with no contact
 * field). Returns '' when neither is present.
 */
export function applicantEmail(
  schema: FieldDef[],
  data: Record<string, unknown>,
): string {
  const fromContact = primaryContactValue(
    schema,
    data,
    (s) => s.type === 'email' || s.id.toLowerCase().includes('email'),
  );
  if (fromContact) return fromContact;
  const field = schema.find((f) => f.type === 'email' && f.audience === 'customer');
  const v = field ? data[field.id] : undefined;
  return typeof v === 'string' ? v : '';
}

function blank(v: unknown): boolean {
  return (
    v === undefined ||
    v === null ||
    (typeof v === 'string' && v.trim() === '')
  );
}

/**
 * Validation errors for a repeater value (array of items). Checks the minimum
 * item count and each item's required sub-fields.
 */
export function repeaterErrors(
  field: Pick<FieldDef, 'subFields' | 'itemLabel' | 'minItems' | 'required' | 'type'>,
  value: unknown,
  /**
   * Addresses whose required lines aren't checked: a contact address that's a
   * copy of one ("Same as registered address" with Companies House's address,
   * often no county). See `filledAddressKeys`.
   */
  lenientAddresses: readonly string[] = [],
): string[] {
  const items = Array.isArray(value) ? (value as RepeaterItem[]) : [];
  const subs = resolveRepeaterSubFields(field);
  const itemLabel = field.itemLabel?.trim() || 'item';
  const min = field.minItems ?? (field.required ? 1 : 0);
  const errors: string[] = [];

  const filled = items.filter((it) =>
    subs.some((s) => !blank(it?.[s.id])),
  );
  if (filled.length < min) {
    errors.push(`Add at least ${min} ${itemLabel}${min === 1 ? '' : 's'}.`);
  }
  filled.forEach((it, i) => {
    for (const s of subs) {
      if (s.type === 'address') {
        if (lenientAddresses.includes(addressKey(it?.[s.id]))) continue;
        for (const m of missingAddressComponents({ address: s.address }, it?.[s.id])) {
          errors.push(`${itemLabel} ${i + 1}: ${m} is required.`);
        }
        continue;
      }
      if (s.required && blank(it?.[s.id])) {
        errors.push(`${itemLabel} ${i + 1}: ${s.label} is required.`);
      }
    }
  });
  return errors;
}

/** An address value's lines as one comparable key ('' parts when missing). */
export function addressKey(v: unknown): string {
  const o = v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
  return ADDRESS_COMPONENT_ORDER.map((k) => (typeof o[k] === 'string' ? (o[k] as string).trim() : '')).join('\u0000');
}

/** The keys of the (non-empty) addresses in `filled`, e.g. Companies House's. */
export function filledAddressKeys(
  fields: FieldDef[],
  values: Record<string, unknown>,
  filled: ReadonlySet<string>,
): string[] {
  const empty = addressKey({});
  return fields
    .filter((f) => f.type === 'address' && filled.has(f.id))
    .map((f) => addressKey(values[f.id]))
    .filter((k) => k !== empty);
}

// ---------------------------------------------------------------------------
// Address field configuration
// ---------------------------------------------------------------------------

/** The sub-fields a composite address can contain, in display order. */
export type AddressComponent =
  | 'line1'
  | 'line2'
  | 'line3'
  | 'city'
  | 'county'
  | 'postcode'
  | 'country';

export const ADDRESS_COMPONENT_ORDER: AddressComponent[] = [
  'line1',
  'line2',
  'line3',
  'city',
  'county',
  'postcode',
  'country',
];

export const ADDRESS_COMPONENT_LABELS: Record<AddressComponent, string> = {
  line1: 'Address line 1',
  line2: 'Address line 2',
  line3: 'Address line 3',
  city: 'Town / City',
  county: 'County',
  postcode: 'Postcode',
  country: 'Country',
};

export interface AddressComponentConfig {
  key: AddressComponent;
  enabled: boolean;
  required: boolean;
  /** Optional label override. */
  label?: string;
}

export interface AddressConfig {
  components: AddressComponentConfig[];
  /**
   * A question (a dropdown of iQ countries) whose answer pre-fills this
   * address's Country line. Only applies while the Country line is asked for;
   * the applicant can still change it.
   */
  countryFrom?: string;
  /**
   * Postcode look-up (Ideal Postcodes): on for every address asking for a
   * postcode unless this is `false`. See `offersPostcodeLookup`.
   */
  postcodeLookup?: boolean;
}

/** Default address layout when a field has no explicit config. */
export function defaultAddressComponents(): AddressComponentConfig[] {
  return [
    { key: 'line1', enabled: true, required: true },
    { key: 'line2', enabled: true, required: false },
    { key: 'line3', enabled: true, required: false },
    { key: 'city', enabled: true, required: true },
    { key: 'county', enabled: true, required: false },
    { key: 'postcode', enabled: true, required: true },
    { key: 'country', enabled: false, required: false },
  ];
}

/** Resolve a field's address components (config or default), in display order. */
export function resolveAddressComponents(
  field: Pick<FieldDef, 'address'>,
): AddressComponentConfig[] {
  const byKey = new Map(
    (field.address?.components ?? []).map((c) => [c.key, c] as const),
  );
  const base = defaultAddressComponents();
  // Emit in canonical order, using the configured component where present and
  // filling any missing keys from the defaults.
  return ADDRESS_COMPONENT_ORDER.map(
    (key) => byKey.get(key) ?? base.find((b) => b.key === key)!,
  );
}

export function addressComponentLabel(c: AddressComponentConfig): string {
  return c.label?.trim() || ADDRESS_COMPONENT_LABELS[c.key];
}

const isBlank = (v: unknown): boolean =>
  v === undefined ||
  v === null ||
  (typeof v === 'string' && v.trim() === '');

/** True when the address names a country other than the UK. */
export function isNonUkAddress(value: unknown): boolean {
  if (!value || typeof value !== 'object') return false;
  const country = (value as Record<string, unknown>).country;
  if (typeof country !== 'string' || !country.trim()) return false;
  return !['GB', 'UK'].includes(country.trim().toUpperCase());
}

/**
 * Whether an address line must be filled for this value. County is only asked
 * of UK addresses (Hayden, 26/10/2026): most countries have no counties, so a
 * required County is optional once the address names another country.
 */
export function addressComponentRequired(
  c: Pick<AddressComponentConfig, 'key' | 'required'>,
  value: unknown,
): boolean {
  if (!c.required) return false;
  return !(c.key === 'county' && isNonUkAddress(value));
}

/**
 * Labels of address components that are enabled + required but empty in `value`.
 * Used for validation on both the renderer and the Worker.
 */
export function missingAddressComponents(
  field: Pick<FieldDef, 'address'>,
  value: unknown,
): string[] {
  const v =
    value && typeof value === 'object'
      ? (value as Record<string, unknown>)
      : {};
  const missing: string[] = [];
  for (const c of resolveAddressComponents(field)) {
    if (!c.enabled || !addressComponentRequired(c, v)) continue;
    if (isBlank(v[c.key])) missing.push(addressComponentLabel(c));
  }
  return missing;
}
